import { useCallback } from 'react';

import {
  AvatarProps,
  MenuItemProps,
  MenuList,
  MenuListProps,
  Menu,
  MenuItem,
  Skeleton,
} from '@vimeo/bokeh';

import { ParsedMentionSuggestion } from '../../types';
import { MentionListItem } from './MentionListItem';

export const MentionListSkeleton = (mentionListProps: Partial<MenuListProps>) => {
  const SkeletonCircle = <Skeleton w="sm" h="sm" borderRadius="round" />;
  const SkeletonBar = <Skeleton borderRadius="pill" w="100%" h="sm" bgSize="200%" />;

  return (
    // @ts-expect-error stragey is set to null because there are two popper instances used
    // at the Popover and Menu component.
    <Menu autoSelect={false} autoFocus={false} strategy={null} isOpen>
      <MenuList {...mentionListProps} gap="0">
        <MenuItem gap="2">
          {SkeletonCircle}
          {SkeletonBar}
        </MenuItem>
        <MenuItem gap="2">
          {SkeletonCircle}
          {SkeletonBar}
        </MenuItem>
      </MenuList>
    </Menu>
  );
};

export const MentionList = ({
  selectedIndex = 0,
  isOpen,
  isLoading,
  avatarSize,
  icon,
  iconSpacing,
  mentionSuggestions,
  onMenuItemSelection,
  ...mentionListProps
}: {
  selectedIndex?: number;
  isOpen?: boolean;
  isLoading?: boolean;
  mentionSuggestions: ParsedMentionSuggestion[];
  avatarSize: AvatarProps['size'];
  onMenuItemSelection: (index: number) => void;
} & MenuListProps &
  Pick<MenuItemProps, 'iconSpacing' | 'icon'>) => {
  const getFocusAttrs = useCallback(
    (currentIndex: number) => {
      if (selectedIndex === currentIndex) {
        return {
          'data-focus': true,
        };
      }

      return {};
    },
    [selectedIndex],
  );

  if (isLoading || !mentionSuggestions.length) return <MentionListSkeleton {...mentionListProps} />;

  return (
    <Menu
      autoSelect={false}
      // @ts-expect-error stragey is set to null because there are two popper instances used at the Popover and Menu component.
      strategy={null}
      isOpen={isOpen}
    >
      <MenuList {...mentionListProps} rootProps={{ minWidth: '0px !important' }}>
        {mentionSuggestions.map(({ name, email, isPending, src, srcSet }, index) => (
          <MentionListItem
            onClick={() => onMenuItemSelection(index)}
            src={src}
            srcSet={srcSet}
            key={`mention-item-${index}`}
            avatarSize={avatarSize}
            iconSpacing={iconSpacing}
            name={name}
            email={email}
            isPending={isPending}
            wordBreak="break-all"
            {...getFocusAttrs(index)}
          />
        ))}
      </MenuList>
    </Menu>
  );
};
